// ---------------------------------------------------------------------------
// Recorte da LATERAL (Preço x Força) sobre a CÓPIA QUE ESTÁ NA GPU.
//
// A lateral desenha o lote inteiro com o X achatado e mostra só a janela visível por setDrawRange (busca binária
// no X dos pontos). Antes o recorte lia o array do React (U.wallSmoothedPoints / sma*Pts). Esse array é uma vista
// de um buffer persistente que o React REESCREVE NO LUGAR durante o render adiado do lote seguinte; enquanto esse
// render não é commitado, a GPU ainda tem a cópia antiga e o recorte era calculado sobre dados que não estão na
// tela (até 131 velas de erro de borda medidos no celular).
//
// Agora o recorte lê o próprio atributo de posição do FastLine (a cópia que o FastLine enviou à GPU) e a contagem de
// pontos válidos dessa cópia. A contagem vem do próprio upload: a cada envio o FastLine troca a versão do atributo e
// põe drawRange = (0, n) (FastLine.tsx, efeito de upload). Como o recorte roda num useFrame (antes do render) e o
// upload num efeito do React (fora do quadro), o primeiro recorte depois de um upload sempre vê esse (0, n).
// Zero alocação por quadro. Não altera o FastLine nem o array do React.
// ---------------------------------------------------------------------------

/** Estado por linha: qual atributo/versão foi visto e quantos pontos válidos a cópia da GPU tem. */
export interface GpuCopy { attr: any; ver: number; n: number; }
export const newGpuCopy = (): GpuCopy => ({ attr: null, ver: -1, n: 0 });

/** Atualiza (só quando houve upload) e devolve a contagem de pontos válidos da cópia da GPU. */
export function gpuCount(geometry: any, st: GpuCopy): number {
  const pos = geometry.attributes.position;
  if (!pos) { st.attr = null; st.ver = -1; st.n = 0; return 0; }
  if (pos !== st.attr || pos.version !== st.ver) {
    st.attr = pos; st.ver = pos.version;
    const c = geometry.drawRange.count;                    // posto pelo upload: (0, n)
    st.n = isFinite(c) && c >= 0 ? Math.min(c, pos.count) : pos.count;
  }
  return st.n;
}

/** Primeiro índice i em [0, n) com x(i) >= x (pontos em ordem crescente de X). */
export function firstAtOrAbove(pts: ArrayLike<number>, n: number, x: number): number {
  let lo = 0, hi = n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (pts[mid * 3] < x) lo = mid + 1; else hi = mid; }
  return lo;
}

/**
 * Recorta a linha à janela [win[0], win[1]] sobre a cópia da GPU (1 ponto antes e 1 depois: a linha chega à borda).
 * A janela vem num Float64Array para não criar números em caixa (alocação) a cada quadro.
 */
export function clipToX(line: any, st: GpuCopy, win: Float64Array): void {
  if (!line) return;
  const xLo = win[0], xHi = win[1];
  const g = line.geometry;
  const n = gpuCount(g, st);
  if (n < 2) return;
  const pts = st.attr.array;
  // duas buscas binárias escritas aqui mesmo (passar o X fracionário para outra função criaria números em caixa)
  let lo = 0, hi = n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (pts[mid * 3] < xLo) lo = mid + 1; else hi = mid; }
  const a = lo > 0 ? lo - 1 : 0;                                  // 1 ponto antes: a linha chega até a borda
  lo = a; hi = n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (pts[mid * 3] < xHi) lo = mid + 1; else hi = mid; }
  const b = lo < n - 1 ? lo : n - 1;                              // 1 ponto depois
  g.setDrawRange(a, b - a + 1 > 0 ? b - a + 1 : 0);
}
